import React, { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Avatar, IconButton } from '@mui/material';
import { Add, Home, Chat, Star, BarChart, Assessment, Settings } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { EventType } from './types';
import { CATEGORY_META, QUICK_ADD } from './constants';
import { useClientPlan } from '@/hooks/useClientPlan';

interface BottomBarProps {
  onQuickAdd?: (type: EventType) => void;
}

const ANGLES = [-90, -54, -18, 18, 54, 90];
const RADIUS = 140;

export const BottomBar: React.FC<BottomBarProps> = ({ onQuickAdd }) => {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((v) => !v), []);
  const navigate = useNavigate();
  const location = useLocation();
  const { hasFeature, loading: featuresLoading } = useClientPlan();

  // Умовна заміна в тих самих 2 слотах — завжди 4 позиції, без "дірок" у верстці
  // (фіксований flex на 4 пункти), але з реальним доступом до Чату/Експертів,
  // коли фіча є. Якщо фічі нема (або ще не завантажилась) — Метрики/Звіти
  // замість Чату/Експертів, вони завжди доступні незалежно від плану.
  const NAV_ITEMS = [
    { icon: <Home />, label: 'Головна', path: '/dashboard' },
    !featuresLoading && hasFeature('chat')
      ? { icon: <Chat />, label: 'Чат', path: '/chat' }
      : { icon: <BarChart />, label: 'Метрики', path: '/metrics' },
    !featuresLoading && hasFeature('expert_connections')
      ? { icon: <Star />, label: 'Експерти', path: '/experts' }
      : { icon: <Assessment />, label: 'Звіти', path: '/reports' },
    { icon: <Settings />, label: 'Налаштування', path: '/settings' },
  ];

  return (
    <>
      {/* Backdrop */}
      {open && (
        <Box
          onClick={toggle}
          sx={{ position: 'fixed', inset: 0, zIndex: 1099, bgcolor: alpha('#000', 0.25) }}
        />
      )}

      {/* Radial buttons */}
      {QUICK_ADD.map(({ type, label }, idx) => {
        const rad = (ANGLES[idx] * Math.PI) / 180;
        const x = RADIUS * Math.sin(rad);
        const y = -RADIUS * Math.cos(rad);
        const meta = CATEGORY_META[type];

        return (
          <Box
            key={type}
            sx={{
              position: 'fixed',
              bottom: 28,
              left: '50%',
              zIndex: 1101,
              pointerEvents: open ? 'auto' : 'none',
              transform: open
                ? `translate(calc(-50% + ${x}px), ${y}px) scale(1)`
                : `translate(-50%, 0) scale(0)`,
              opacity: open ? 1 : 0,
              transition: `transform 0.25s cubic-bezier(.34,1.56,.64,1) ${idx * 35}ms,
                           opacity 0.2s ease ${idx * 35}ms`,
            }}
          >
            <Box
              onClick={() => { toggle(); onQuickAdd?.(type); }}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
            >
              <Avatar sx={{ width: 60, height: 60, bgcolor: meta.color, boxShadow: 3, '& svg': { fontSize: 28 }, '&:hover': { boxShadow: 6 } }}>
                {meta.icon}
              </Avatar>
              <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600, color: 'white', bgcolor: alpha(meta.color, 0.85), px: 1, py: 0.25, borderRadius: 1, whiteSpace: 'nowrap' }}>
                {label}
              </Typography>
            </Box>
          </Box>
        );
      })}

      {/* Bottom nav */}
      <Box sx={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        height: 64,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex', alignItems: 'center', justifyContent: 'space-around',
        px: 1,
        zIndex: 1100,
      }}>
        {NAV_ITEMS.slice(0, 2).map(({ icon, label, path }) => {
          const active = location.pathname === path;
          return (
            <Box key={path} onClick={() => navigate(path)} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, cursor: 'pointer', flex: 1 }}>
              <IconButton sx={{ color: active ? 'primary.main' : 'text.secondary', '& svg': { fontSize: 28 } }}>
                {icon}
              </IconButton>
              <Typography variant="caption" sx={{ fontSize: 10, color: active ? 'primary.main' : 'text.secondary', fontWeight: active ? 600 : 400 }}>
                {label}
              </Typography>
            </Box>
          );
        })}

        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <IconButton
            onClick={toggle}
            sx={{
              width: 52, height: 52,
              bgcolor: open ? 'error.main' : 'primary.main',
              color: 'common.white',
              boxShadow: 4,
              transition: 'background-color 0.2s ease',
              '&:hover': { bgcolor: open ? 'error.dark' : 'primary.dark' },
            }}
          >
            <Box sx={{ transform: open ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease', display: 'flex' }}>
              <Add />
            </Box>
          </IconButton>
        </Box>

        {NAV_ITEMS.slice(2).map(({ icon, label, path }) => {
          const active = location.pathname === path;
          return (
            <Box key={path} onClick={() => navigate(path)} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, cursor: 'pointer', flex: 1 }}>
              <IconButton sx={{ color: active ? 'primary.main' : 'text.secondary', '& svg': { fontSize: 28 } }}>
                {icon}
              </IconButton>
              <Typography variant="caption" sx={{ fontSize: 10, color: active ? 'primary.main' : 'text.secondary', fontWeight: active ? 600 : 400 }}>
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </>
  );
};
